import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import { Flex, Spinner, Text, IconButton, Badge } from "@radix-ui/themes";
import { ArrowLeftIcon } from "@radix-ui/react-icons";
import { FileText } from "lucide-react";
import { PageLayoutV2 } from "@/v2/components/layout/page-layout/PageLayout";
import { PageHeader } from "@/v2/components/layout";
import { usePagesApi } from "./Pages.api";
import dayjs from "dayjs";
import { useMemo, useState, useCallback } from "react";
import {
  DateRangePicker,
  DateRange,
} from "@/components/shared/date-range-picker/DateRangePicker";
import styles from "./Pages.module.scss";

const PageDetail = () => {
  const { pageId } = useParams<{ pageId: string }>();
  const navigate = useNavigate();
  const pagesApi = usePagesApi();

  const [dateRange, setDateRange] = useState<DateRange>(() => ({
    dateFrom: dayjs().startOf("day").subtract(7, "day").toISOString(),
    dateTo: dayjs().toISOString(),
  }));

  const handleDateRangeChange = useCallback((newDateRange: DateRange) => {
    setDateRange({
      dateFrom: newDateRange.dateFrom,
      dateTo: dayjs().toISOString(),
    });
  }, []);

  const { data: pages, isLoading } = useQuery({
    queryKey: ["pages", dateRange.dateFrom, dateRange.dateTo],
    queryFn: () =>
      pagesApi.getPagesStats({
        startDate: dateRange.dateFrom,
        endDate: dateRange.dateTo,
      }),
    refetchOnWindowFocus: false,
  });

  const { data: screenshot } = useQuery({
    queryKey: ["pageScreenshot", pageId],
    queryFn: () => pagesApi.getPageScreenshot(pageId!),
    enabled: !!pageId,
    refetchOnWindowFocus: false,
  });

  const page = useMemo(() => {
    return pages?.find((p) => p.id === pageId);
  }, [pages, pageId]);

  const handleBack = useCallback(() => {
    navigate("/pages");
  }, [navigate]);

  if (isLoading) {
    return (
      <PageLayoutV2>
        <Flex justify="center" align="center" py="9">
          <Spinner size="3" />
        </Flex>
      </PageLayoutV2>
    );
  }

  if (!page) {
    return (
      <PageLayoutV2>
        <Flex justify="center" align="center" py="9" direction="column" gap="4">
          <Text size="3" color="gray" highContrast>
            Page not found
          </Text>
          <IconButton onClick={handleBack}>
            <ArrowLeftIcon />
          </IconButton>
        </Flex>
      </PageLayoutV2>
    );
  }

  return (
    <PageLayoutV2>
      <Flex direction="column">
        <PageHeader
          title={
            <Flex align="center" gap="2">
              <span>{page.name}</span>
              {page.isNew && (
                <Badge size="1" color="orange" variant="soft">
                  New
                </Badge>
              )}
            </Flex>
          }
          description="Page details and guide configuration"
          icon={<FileText size={24} />}
          actions={
            <Flex gap="4" align="center">
              <IconButton onClick={handleBack} variant="soft">
                <ArrowLeftIcon />
              </IconButton>
              <DateRangePicker
                value={dateRange}
                onChange={handleDateRangeChange}
              />
            </Flex>
          }
        />
      </Flex>

      <Flex px="6" direction="column" pt="4" gap="4">
        <Flex direction="column" gap="2">
          <Text size="4" weight="bold" highContrast>
            Screenshot
          </Text>
          {screenshot ? (
            <img
              src={screenshot}
              alt="Page screenshot"
              className={styles.screenshot}
            />
          ) : (
            <Text size="3" color="gray">
              No screenshot
            </Text>
          )}
        </Flex>

        {page.pageGuide ? (
          <div className={styles.container}>
            <Flex direction="column" gap="4">
              <Flex direction="column" gap="2">
                <Text size="4" weight="bold" highContrast>
                  Overview
                </Text>
                <Text size="3" color="gray">
                  {page.pageGuide.overview}
                </Text>
              </Flex>

              <Flex direction="column" gap="2">
                <Text size="4" weight="bold" highContrast>
                  Assistant Goal
                </Text>
                <Text size="3" color="gray">
                  {page.pageGuide.assistantGoal}
                </Text>
              </Flex>

              {page.pageGuide.nextActionsSuggestions.length > 0 && (
                <Flex direction="column" gap="2">
                  <Text size="4" weight="bold" highContrast>
                    Next Actions Suggestions
                  </Text>
                  <Flex direction="column" gap="1">
                    {page.pageGuide.nextActionsSuggestions.map(
                      (suggestion: string, index: number) => (
                        <Text key={index} size="3" color="gray">
                          • {suggestion}
                        </Text>
                      )
                    )}
                  </Flex>
                </Flex>
              )}

              {page.pageGuide.skills.length > 0 && (
                <Flex direction="column" gap="2">
                  <Text size="4" weight="bold" highContrast>
                    Skills
                  </Text>
                  <Flex direction="column" gap="1">
                    {page.pageGuide.skills.map((skill, index) => (
                      <Text key={index} size="3" color="gray">
                        • {skill}
                      </Text>
                    ))}
                  </Flex>
                </Flex>
              )}
            </Flex>
          </div>
        ) : (
          <Flex justify="center" align="center" py="9">
            <Text size="2" color="gray" highContrast>
              No guide configured for this page
            </Text>
          </Flex>
        )}
      </Flex>
    </PageLayoutV2>
  );
};

export default PageDetail;
